// src/lib/http.js
import axios from "axios";
import { LoadingController } from "../components/GlobalFunction/LoadingOverlay";

/* ============================================================================
   CRA ENV (WordPress only)
   - REACT_APP_WP_GRAPHQL_URL (ex: http://localhost/wp-headless/graphql)
   - REACT_APP_WP_ORIGIN      (ex: http://localhost/wp-headless)  ✅ preferred
============================================================================ */

export const WP_GRAPHQL_URL = String(
  process.env.REACT_APP_WP_GRAPHQL_URL || "",
).trim();

const ENV_WP_ORIGIN = String(process.env.REACT_APP_WP_ORIGIN || "")
  .trim()
  .replace(/\/+$/, "");

/* ============================================================================
   LAN-safe origin resolution
   Problem:
   - When you open frontend via LAN (http://192.168.x.x:3000),
     any API URL that still uses "localhost" will target the *client device*,
     not your XAMPP machine -> ERR_CONNECTION_REFUSED.

   Goal:
   - If env/derived points to localhost but we're browsing via LAN IP/hostname,
     rewrite WP_BASE to use current hostname.
============================================================================ */

function isLocalhostHostname(hostname) {
  const h = String(hostname || "")
    .trim()
    .toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "::1";
}

function isLocalhostUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return false;
  try {
    const u = new URL(raw);
    return isLocalhostHostname(u.hostname);
  } catch {
    // fallback string checks
    return (
      /(^|\/\/)localhost(\:|\/|$)/i.test(raw) ||
      /(^|\/\/)127\.0\.0\.1(\:|\/|$)/i.test(raw) ||
      /(^|\/\/)\[::1\](\:|\/|$)/i.test(raw)
    );
  }
}

function deriveWpBaseFromGraphqlUrl(graphqlUrl) {
  const raw = String(graphqlUrl || "").trim();
  if (!raw) return "";

  try {
    const u = new URL(raw);
    // remove trailing "/graphql"
    const path = u.pathname.replace(/\/graphql\/?$/i, "");
    return `${u.origin}${path}`.replace(/\/+$/, "");
  } catch {
    return raw.replace(/\/graphql\/?$/i, "").replace(/\/+$/, "");
  }
}

function defaultWpBaseFromWindow() {
  // WP is typically on same host (port 80/443), not React dev port.
  // This is the safest LAN behavior: follow the host you're visiting.
  return `${window.location.protocol}//${window.location.hostname}/wp-headless`;
}

function toWpHeadlessBaseFromHost(host) {
  const hostname = String(host || "").trim();
  if (!hostname) return defaultWpBaseFromWindow();
  return `${window.location.protocol}//${hostname}/wp-headless`;
}

/**
 * ✅ Choose WP_BASE in a LAN-safe way:
 * - If ENV/derived is localhost but we are visiting via LAN IP/hostname, ignore env/derived
 * - Otherwise honor env / derived graphql base / fallback window
 */
function resolveWpBase() {
  const windowHost = String(window.location.hostname || "").trim();
  const visitingViaLocalhost = isLocalhostHostname(windowHost);

  const derived = deriveWpBaseFromGraphqlUrl(WP_GRAPHQL_URL);

  // If the user is NOT visiting via localhost (meaning they are on LAN IP / hostname),
  // then any "localhost" WP origin would break other devices.
  // So we force WP_BASE to follow windowHost.
  if (!visitingViaLocalhost) {
    if (ENV_WP_ORIGIN && isLocalhostUrl(ENV_WP_ORIGIN)) {
      return toWpHeadlessBaseFromHost(windowHost);
    }
    if (derived && isLocalhostUrl(derived)) {
      return toWpHeadlessBaseFromHost(windowHost);
    }
  }

  // normal priority
  const isDev = process.env.NODE_ENV === "development";
  const visitingViaPrivateLan =
    /^192\.168\./.test(windowHost) ||
    /^10\./.test(windowHost) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(windowHost);

  // In local LAN development, use the same LAN host for WP.
  // Example:
  // frontend: http://192.168.0.107:3000
  // WP:       http://192.168.0.107/wp-headless
  if (isDev && visitingViaPrivateLan) {
    return toWpHeadlessBaseFromHost(windowHost);
  }

  // normal priority
  return ENV_WP_ORIGIN || derived || defaultWpBaseFromWindow();
}

/**
 * ✅ WP_BASE is the WordPress site base
 * Examples:
 *  - http://localhost/wp-headless
 *  - http://192.168.0.102/wp-headless
 *  - https://yourdomain.com
 */
export const WP_BASE = resolveWpBase().replace(/\/+$/, "");

/**
 * ✅ REST_BASE is your plugin namespace base:
 *   {WP_BASE}/wp-json/seho/v1
 */
export const REST_BASE = `${String(WP_BASE || "").replace(/\/+$/, "")}/wp-json/api`;

/* ============================================================================

   URL helpers (prevent broken images due to spaces or bad encoding)

============================================================================ */

function safeEncodeUrl(full) {
  try {
    const u = new URL(full);
    u.pathname = u.pathname
      .split("/")
      .map((seg) => encodeURIComponent(decodeURIComponent(seg)))
      .join("/");
    return u.toString();
  } catch {
    return String(full || "").replace(/ /g, "%20");
  }
}

/** normalize to "/..." */
function ensureLeadingSlash(p) {
  const s = String(p || "")
    .trim()
    .replace(/\\/g, "/");
  if (!s) return "";
  return s.startsWith("/") ? s : `/${s}`;
}

function getWpParts() {
  const wpBaseNorm = String(WP_BASE || "").replace(/\/+$/, "");
  try {
    const u = new URL(wpBaseNorm);
    return {
      wpBaseNorm,
      originOnly: u.origin,
      wpPathBase: u.pathname.replace(/\/+$/, ""), // "/wp-headless" or ""
    };
  } catch {
    return { wpBaseNorm, originOnly: "", wpPathBase: "" };
  }
}

/**
 * Detect whether a string looks like a WP media/asset path.
 * - "/wp-content/... "
 * - "/uploads/..." (legacy storage from old system)
 * - "/wp-headless/wp-content/..."
 */
export function isWpAssetPath(url) {
  if (!url) return false;
  const raw = String(url).trim();
  if (!raw) return false;

  // absolute -> check pathname
  if (/^https?:\/\//i.test(raw)) {
    try {
      const u = new URL(raw);
      return (
        u.pathname.startsWith("/wp-content/") ||
        u.pathname.startsWith("/uploads/") ||
        u.pathname.startsWith("/wp-headless/wp-content/") ||
        /\/wp-content\//i.test(u.pathname)
      );
    } catch {
      return /\/wp-content\//i.test(raw) || /\/uploads\//i.test(raw);
    }
  }

  // relative
  return (
    raw.startsWith("/wp-content/") ||
    raw.startsWith("wp-content/") ||
    raw.startsWith("/uploads/") ||
    raw.startsWith("uploads/") ||
    raw.startsWith("/wp-headless/wp-content/")
  );
}

/**
 * ✅ Resolve WP asset URLs into a valid absolute URL under WP_BASE.
 * Supports legacy "/uploads/..." by mapping to:
 *   {WP_BASE}/wp-content/uploads/...
 */
export function resolveWpAssetUrl(url) {
  if (!url) return "";
  const raw0 = String(url).trim();
  if (!raw0) return "";

  const { wpBaseNorm, originOnly, wpPathBase } = getWpParts();

  // absolute already
  if (/^https?:\/\//i.test(raw0)) {
    try {
      const u = new URL(raw0);

      // legacy absolute "/uploads/..." -> wp-content/uploads
      if (u.pathname.startsWith("/uploads/")) {
        const out = `${wpBaseNorm}/wp-content${u.pathname}${u.search || ""}`;
        return safeEncodeUrl(out);
      }

      // normal wp-content/uploads
      if (u.pathname.startsWith("/wp-content/uploads/")) {
        const out = `${wpBaseNorm}${u.pathname}${u.search || ""}`;
        return safeEncodeUrl(out);
      }

      // sometimes stored with "/wp-headless/wp-content/uploads/.."
      if (u.pathname.startsWith("/wp-headless/wp-content/uploads/")) {
        const normalizedPath = u.pathname.replace(/^\/wp-headless/, "");
        const out = `${wpBaseNorm}${normalizedPath}${u.search || ""}`;
        return safeEncodeUrl(out);
      }

      // other absolute URLs: keep as-is
      return safeEncodeUrl(raw0);
    } catch {
      return safeEncodeUrl(raw0);
    }
  }

  const raw = ensureLeadingSlash(raw0);

  // legacy relative "/uploads/..." -> WP uploads
  if (raw.startsWith("/uploads/")) {
    return safeEncodeUrl(`${wpBaseNorm}/wp-content${raw}`);
  }

  // "/wp-headless/wp-content/..." => attach to origin only if possible
  if (wpPathBase && raw.startsWith(`${wpPathBase}/wp-content/`)) {
    const out = originOnly ? `${originOnly}${raw}` : `${wpBaseNorm}${raw}`;
    return safeEncodeUrl(out);
  }

  // "/wp-content/..." => attach to WP_BASE
  if (raw.startsWith("/wp-content/")) {
    return safeEncodeUrl(`${wpBaseNorm}${raw}`);
  }

  // "wp-content/..." => attach to WP_BASE + "/"
  if (raw0.startsWith("wp-content/")) {
    return safeEncodeUrl(`${wpBaseNorm}/${raw0}`);
  }

  // fallback: treat as WP relative
  if (raw.startsWith("/")) return safeEncodeUrl(`${wpBaseNorm}${raw}`);
  return safeEncodeUrl(`${wpBaseNorm}/${raw0}`);
}

/**
 * ✅ Resolve “public” URLs in this project:
 * - WP assets => WP_BASE
 * - relative paths => WP_BASE (because Node backend is removed)
 * - absolute external URLs => keep
 */
export function resolvePublicUrl(url) {
  if (!url) return "";
  const raw = String(url).trim();
  if (!raw) return "";

  // WP asset patterns (including /uploads legacy)
  if (isWpAssetPath(raw)) return resolveWpAssetUrl(raw);

  // absolute external
  if (/^https?:\/\//i.test(raw)) return safeEncodeUrl(raw);

  // any other relative path: treat as WP relative (node removed)
  const { wpBaseNorm } = getWpParts();
  if (raw.startsWith("/")) return safeEncodeUrl(`${wpBaseNorm}${raw}`);
  return safeEncodeUrl(`${wpBaseNorm}/${raw}`);
}

/**
 * Alias helper used around the UI
 */
export function resolveAssetUrl(url) {
  return resolvePublicUrl(url);
}

/* ============================================================================

   Axios instance (WordPress REST only)

============================================================================ */

export const http = axios.create({
  baseURL: REST_BASE,
  withCredentials: false,
});

/* ============================================================================

   Interceptors (keep your global loading behavior)

============================================================================ */

http.interceptors.request.use((config) => {
  const isFormData =
    typeof FormData !== "undefined" && config.data instanceof FormData;

  if (isFormData) {
    if (config.headers) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  } else {
    config.headers = config.headers || {};
    if (!config.headers["Content-Type"] && !config.headers["content-type"]) {
      config.headers["Content-Type"] = "application/json";
    }
  }

  if (!config.skipLoading) {
    LoadingController.start();
    config.__withGlobalLoading = true;
  }
  return config;
});

function stopIfNeeded(config) {
  if (config && config.__withGlobalLoading) LoadingController.stop();
}

http.interceptors.response.use(
  (res) => {
    stopIfNeeded(res.config);
    return res;
  },
  (err) => {
    stopIfNeeded(err?.config);
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Request error";
    const wrapped = new Error(msg);
    if (err.response) wrapped.response = err.response;
    if (err.config) wrapped.config = err.config;
    wrapped.code = err.code || err.response?.status;
    return Promise.reject(wrapped);
  },
);

// console.log("[HTTP] window.hostname:", window.location.hostname);
// console.log("[HTTP] WP_BASE:", WP_BASE);
// console.log("[HTTP] REST_BASE:", REST_BASE);
// console.log("[HTTP] WP_GRAPHQL_URL:", WP_GRAPHQL_URL);
// console.log("[HTTP] WP_ORIGIN (env):", ENV_WP_ORIGIN);
